'use strict';
Object.defineProperty(exports, "__esModule", { value: true });
var _ = require("lodash");
var analyzerUtils_1 = require("./analyzerUtils");
var coreUtils_1 = require("coreUtils");
var EVENTS_TYPES = {
    START_EVENT: 'start',
    END_EVENT: 'end'
};
var COMPONENT_SIZE_SCALE_FACTOR = 0.9;
var DISTANCE_PARTITIONS_WEIGHTS = {
    HORIZONTAL: 1,
    VERTICAL: 1.25
};
function findVerticalDividerFragmentIndex(fragments, allEvents) {
    var eventsCoordinates = _(allEvents.y).partition({ eventType: EVENTS_TYPES.START_EVENT }).map(function (a) { return _.map(a, 'coordinate'); }).value();
    var maxStart = _.max(eventsCoordinates[0]);
    var minEnd = _.min(eventsCoordinates[1]);
    return _.findIndex(fragments, function (fragment) {
        var firstComp = fragment.comps[0];
        return fragment.comps.length === 1 && firstComp.conversionData.keepIfVerticalDivider &&
            firstComp.layout.y <= minEnd && analyzerUtils_1.getComponentEndCoordinate(firstComp, 'y') >= maxStart;
    });
}
function refragment(fragments, getNewFragmentIndex) {
    return _.transform(fragments, function (res, fragment, fragmentIndex) {
        var newFragmentIndex = getNewFragmentIndex(fragmentIndex);
        res[newFragmentIndex] = res[newFragmentIndex] || { comps: [], distanceToPreviousFragment: fragment.distanceToPreviousFragment };
        res[newFragmentIndex].comps = res[newFragmentIndex].comps.concat(fragment.comps);
        return res;
    }, []);
}
var strategiesHandlers = {
    singleCompRow: function (fragments, allEvents, partitionAxis) {
        if (partitionAxis === 'x') {
            return null;
        }
        var singleCompRowIndex = _.findIndex(fragments, function (fragment) { return _.size(fragment.comps) === 1; });
        if (singleCompRowIndex === -1) {
            return null;
        }
        var getNewFragmentIndex = function (oldFragmentIndex) { return analyzerUtils_1.getSign(oldFragmentIndex - singleCompRowIndex) + analyzerUtils_1.getSign(singleCompRowIndex); };
        var newFragments = refragment(fragments, getNewFragmentIndex);
        return { type: 'singleCompRow', fragments: newFragments, fragmentsEvents: splitAllEventsByFragments(allEvents, newFragments) };
    },
    verticalDivider: function (fragments, allEvents, partitionAxis) {
        var isSingleCompFragment = function (fragment) { return _.size(fragment.comps) === 1; };
        if (_.size(fragments) < 3 || _.every(fragments, isSingleCompFragment) || partitionAxis === 'y') {
            return null;
        }
        var dividerFragmentIndex = findVerticalDividerFragmentIndex(fragments, allEvents);
        if (dividerFragmentIndex === -1) {
            return null;
        }
        _.set(fragments[dividerFragmentIndex].comps[0], ['conversionData', 'isVerticalDivider'], true);
        var getNewFragmentIndex = function (oldFragmentIndex) { return analyzerUtils_1.getSign(oldFragmentIndex - dividerFragmentIndex) + analyzerUtils_1.getSign(dividerFragmentIndex); };
        var newFragments = refragment(fragments, getNewFragmentIndex);
        return { type: 'verticalDivider', fragments: newFragments, fragmentsEvents: splitAllEventsByFragments(allEvents, newFragments) };
    },
    mirrorPattern: function (fragments, allEvents, partitionAxis) {
        if (_.size(fragments) < 2) {
            return null;
        }
        var fragmentsEvents = splitAllEventsByFragments(allEvents, fragments);
        var perpAxis = getPerpendicularAxis(partitionAxis);
        var sortedFragmentsComps = _.map(fragmentsEvents, function (fragmentEvents) { return extractCompsFromEvents(fragmentEvents[perpAxis]); });
        var isMirrorPattern = _.every(sortedFragmentsComps, function (fragmentComps) { return analyzerUtils_1.areSimilarFragments(fragmentComps, sortedFragmentsComps[0], perpAxis); });
        if (!isMirrorPattern) {
            return null;
        }
        return isMirrorPattern ? createSingleFragmentPartition(_.flatten(sortedFragmentsComps), allEvents, { type: 'mirrorPattern' }) : null;
    },
    chessPattern: function (fragments, allEvents, partitionAxis) {
        var isValidFragmentSize = function (fragment) { return _.size(fragment.comps) === 2; };
        if (partitionAxis !== 'y' || _.size(fragments) < 2 || !_.every(fragments, isValidFragmentSize)) {
            return null;
        }
        var fragmentsEvents = splitAllEventsByFragments(allEvents, fragments);
        var xSortedFragmentsComps = _.map(fragmentsEvents, function (fragmentEvents) { return extractCompsFromEvents(fragmentEvents.x); });
        var chessSortedComps = _.flatMap(xSortedFragmentsComps, function (fragmentComps, fragmentIndex) { return isEven(fragmentIndex) ? fragmentComps : _.reverse(fragmentComps); });
        var evenCategory = chessSortedComps[0].conversionData.category;
        var oddCategory = evenCategory === 'text' ? 'photo' : 'text';
        var isValidChessComp = function (comp, compIndex) { return comp.conversionData.category === (isEven(compIndex) ? evenCategory : oddCategory); };
        var isChessPattern = _.every(chessSortedComps, isValidChessComp);
        return isChessPattern ? createSingleFragmentPartition(chessSortedComps, allEvents, { type: 'chessPattern' }) : null;
    }
};
var extractCompsFromEvents = function (events) { return _(events).map('compRef').uniq().value(); };
exports.extractCompsFromEvents = extractCompsFromEvents;
var getPerpendicularAxis = function (axis) { return axis === 'x' ? 'y' : 'x'; };
var isEven = function (n) { return n % 2 === 0; };
function createSingleFragmentPartition(sortedComps, allEvents, partitionData) {
    return _.assign(partitionData, { fragments: [{ comps: sortedComps, distanceToPreviousFragment: -1 }], fragmentsEvents: [allEvents] });
}
function compareEvents(event1, event2) {
    var distance = event1.coordinate - event2.coordinate;
    if (distance !== 0) {
        return analyzerUtils_1.getSign(distance);
    }
    if (event1.compRef === event2.compRef) {
        return event1.eventType === EVENTS_TYPES.START_EVENT ? -1 : 1;
    }
    return event1.eventType === EVENTS_TYPES.END_EVENT ? -1 : 1;
}
function createEventsQueue(comps, axis) {
    var events = _.transform(comps, function (eventsQueue, comp) {
        var componentStartPosition = comp.layout[axis];
        var componentEndPosition = analyzerUtils_1.getComponentEndCoordinate(comp, axis);
        eventsQueue.push({ eventType: EVENTS_TYPES.START_EVENT, compRef: comp, coordinate: componentStartPosition });
        eventsQueue.push({ eventType: EVENTS_TYPES.END_EVENT, compRef: comp, coordinate: componentEndPosition });
        return eventsQueue;
    }, []);
    events.sort(compareEvents);
    return events;
}
exports.createEventsQueue = createEventsQueue;
function createFragments(sweepLineEvents) {
    var sweepLineStatus = [];
    var fragments = [];
    _.forEach(sweepLineEvents, function (event) {
        var lastFragment = _.last(fragments);
        if (event.eventType === EVENTS_TYPES.START_EVENT) {
            if (_.isEmpty(sweepLineStatus)) {
                var distance = lastFragment ? event.coordinate - _.last(lastFragment.events).coordinate : -1;
                var newFragment = { events: [event], distanceToPreviousFragment: distance };
                fragments.push(newFragment);
            }
            else {
                lastFragment.events.push(event);
            }
            sweepLineStatus.push(event);
        }
        else {
            _.remove(sweepLineStatus, { compRef: event.compRef });
            lastFragment.events.push(event);
        }
    });
    return _.map(fragments, function (group) {
        return { comps: extractCompsFromEvents(group.events), distanceToPreviousFragment: group.distanceToPreviousFragment };
    });
}
exports.createFragments = createFragments;
function divideByDistance(fragments, allEvents, partitionAxis) {
    var mostDistantFragment = _.maxBy(fragments, 'distanceToPreviousFragment');
    var mostDistantFragmentIndex = _.findIndex(fragments, mostDistantFragment);
    var getNewFragmentIndex = function (oldFragmentIndex) { return oldFragmentIndex < mostDistantFragmentIndex ? 0 : 1; };
    var newFragments = refragment(fragments, getNewFragmentIndex);
    var adjacentFragments = [fragments[mostDistantFragmentIndex - 1], mostDistantFragment];
    var adjacentFragmentsEvents = _.filter(allEvents[getPerpendicularAxis(partitionAxis)], function (e) { return _.some(adjacentFragments, function (g) { return _.includes(g.comps, e.compRef); }); });
    var partition = { fragmentsEvents: splitAllEventsByFragments(allEvents, newFragments), fragments: newFragments };
    var maxCompsDistance = calculateMaxCompsDistance(adjacentFragments, adjacentFragmentsEvents, partitionAxis);
    return _.assign(partition, { maxCompsDistance: maxCompsDistance });
}
function extractPartitionAxisVectors(eventsPartition, axis) {
    return _(eventsPartition)
        .map(function (events, vectorIndex) {
        return (vectorIndex ?
            _.map(events, function (e) { return _.defaults({ closestPoint: e.compRef.layout[axis] }, e); }) :
            _.map(events, function (e) { return _.defaults({ closestPoint: analyzerUtils_1.getComponentEndCoordinate(e.compRef, axis) }, e); }));
    })
        .map(function (events, vectorIndex) {
        return _.reduce(events, function (vector, e) {
            var lastAddedEvent = _.last(vector);
            if (!lastAddedEvent || lastAddedEvent.eventType === EVENTS_TYPES.END_EVENT) {
                return vector.concat(e.eventType === EVENTS_TYPES.START_EVENT ? [e] : []);
            }
            if (lastAddedEvent.compRef === e.compRef) {
                return vector.concat([e]);
            }
            if (vectorIndex && e.closestPoint < lastAddedEvent.closestPoint || !vectorIndex && e.closestPoint > lastAddedEvent.closestPoint) {
                vector.splice(vector.length - 1, 1, e);
            }
            return vector;
        }, []);
    })
        .value();
}
function calculateMaxCompsDistance(fragments, fragmentsEvents, axis) {
    var eventsPartition = _.partition(fragmentsEvents, function (e) { return _.includes(fragments[0].comps, e.compRef); });
    var events = extractPartitionAxisVectors(eventsPartition, axis);
    var eventIndex = [0, 0];
    var sweepLineStatus = [[], []];
    function getNextEventFragmentIndex() {
        if (eventIndex[0] === events[0].length || eventIndex[1] === events[1].length) {
            return eventIndex[0] === events[0].length ? 1 : 0;
        }
        return events[0][eventIndex[0]].coordinate <= events[1][eventIndex[1]].coordinate ? 0 : 1;
    }
    var maxCompsDistances = -Infinity;
    while (eventIndex[0] < events[0].length || eventIndex[1] < events[1].length) {
        var eventFragmentIndex = getNextEventFragmentIndex();
        var event_1 = events[eventFragmentIndex][eventIndex[eventFragmentIndex]++];
        if (event_1.eventType === EVENTS_TYPES.END_EVENT) {
            _.remove(sweepLineStatus[eventFragmentIndex], { compRef: event_1.compRef });
            continue;
        }
        sweepLineStatus[eventFragmentIndex].push(event_1);
        var closestEvents = [_.maxBy(sweepLineStatus[0], 'closestPoint'), _.minBy(sweepLineStatus[1], 'closestPoint')];
        var distanceBetweenClosestEvents = _.get(closestEvents, [1, 'closestPoint']) - _.get(closestEvents, [0, 'closestPoint']);
        if (!_.isNaN(distanceBetweenClosestEvents) && distanceBetweenClosestEvents > maxCompsDistances) {
            maxCompsDistances = distanceBetweenClosestEvents;
        }
    }
    return maxCompsDistances;
}
function splitAxisEventsByFragments(events, fragments) {
    return _.transform(events, function (res, event) {
        var eventFragmentIndex = _.findIndex(fragments, function (group) { return _.includes(group.comps, event.compRef); });
        res[eventFragmentIndex] = (res[eventFragmentIndex] || []).concat([event]);
        return res;
    }, []);
}
exports.splitAxisEventsByFragments = splitAxisEventsByFragments;
function splitAllEventsByFragments(allEvents, fragments) {
    var xEventsPartition = splitAxisEventsByFragments(allEvents.x, fragments);
    var yEventsPartition = splitAxisEventsByFragments(allEvents.y, fragments);
    return _.map(_.keys(fragments), function (index) { return _.assign({}, { x: xEventsPartition[index], y: yEventsPartition[index] }); });
}
function findBestPartitionByStrategy(allEvents, fragments, strategies) {
    var bestPartition = null;
    _.forEach(strategiesHandlers, function (handler, strategyName) {
        if (!strategies[strategyName]) {
            return true;
        }
        var bestPartitionY = handler(fragments.y, allEvents, 'y');
        var bestPartitionX = handler(fragments.x, allEvents, 'x');
        bestPartition = bestPartitionY && bestPartitionX ? null : bestPartitionY || bestPartitionX;
        return bestPartition === null;
    });
    return bestPartition;
}
function findBestDistancePartition(allEvents, fragments) {
    var partitionX = _.size(fragments.x) > 1 ? divideByDistance(fragments.x, allEvents, 'x') : null;
    var partitionY = _.size(fragments.y) > 1 ? divideByDistance(fragments.y, allEvents, 'y') : null;
    if (partitionX && partitionY) {
        var bestDistancePartition = DISTANCE_PARTITIONS_WEIGHTS.HORIZONTAL * partitionX.maxCompsDistance > DISTANCE_PARTITIONS_WEIGHTS.VERTICAL * partitionY.maxCompsDistance ? partitionX : partitionY;
        return _.pick(bestDistancePartition, ['fragments', 'fragmentsEvents']);
    }
    return partitionX || partitionY;
}
function findBestPartition(allEvents, fragments, strategies) {
    return findBestPartitionByStrategy(allEvents, fragments, strategies) || findBestDistancePartition(allEvents, fragments);
}
function getSortedOverlappingComponents(parent, comps) {
    comps = analyzerUtils_1.sortComponentsByNaturalOrder(parent, comps);
    comps = _.sortBy(comps, 'layout.y');
    var overlayGroupId = "overlayGroup_" + comps[0].id;
    _.forEach(comps, function (comp) { return _.set(comp, ['conversionData', 'overlayGroupId'], overlayGroupId); });
    return comps;
}
function getSortedComponents(parent, comps, allEvents, settings) {
    if (_.size(comps) < 2) {
        return comps || [];
    }
    var fragments = _.mapValues(allEvents, createFragments);
    if (_.size(fragments.x) === 1 && _.size(fragments.y) === 1) {
        return settings.detectSemanticGroups && settings.useOverlapRules ? getSortedOverlappingComponents(parent, comps) :
            getSortedOverlappingComponentsOld(parent, comps, settings);
    }
    var partition = findBestPartition(allEvents, fragments, { mirrorPattern: true, verticalDivider: true, chessPattern: true, singleCompRow: true });
    return _.size(partition.fragments) === 1 ? partition.fragments[0].comps :
        _.flatMap(partition.fragments, function (fragment, fragmentIndex) { return getSortedComponents(parent, fragment.comps, partition.fragmentsEvents[fragmentIndex], settings); });
}
function getSortedOverlappingComponentsOld(parent, comps, settings) {
    var haveTransformedLayout = function (compsList) { return _.some(compsList, 'conversionData.originalLayout'); };
    var isLayoutTransformed = haveTransformedLayout(comps) || _.some(comps, function (child) { return haveTransformedLayout(child); });
    if (isLayoutTransformed) {
        restoreLayouts(comps);
        return comps;
    }
    transformLayouts(comps);
    var eventsY = createEventsQueue(comps, 'y');
    var eventsX = createEventsQueue(comps, 'x');
    var orderedComps = getSortedComponents(parent, comps, { y: eventsY, x: eventsX }, settings);
    restoreLayouts(orderedComps);
    return orderedComps;
}
function transformLayouts(comps) {
    _.forEach(comps, function (comp) {
        var originalLayout = comp.layout;
        var transformedLayout = _.assign(coreUtils_1.objectUtils.cloneDeep(originalLayout), {
            height: originalLayout.height * COMPONENT_SIZE_SCALE_FACTOR,
            width: originalLayout.width * COMPONENT_SIZE_SCALE_FACTOR
        });
        comp.layout = transformedLayout;
        _.set(comp, ['conversionData', 'originalLayout'], originalLayout);
    });
}
function restoreLayouts(comps, recursive) {
    if (recursive === void 0) { recursive = true; }
    _.forEach(comps, function (comp) {
        var originalLayout = _.get(comp, ['conversionData', 'originalLayout']);
        if (originalLayout) {
            comp.layout = coreUtils_1.objectUtils.cloneDeep(originalLayout);
            delete comp.conversionData.originalLayout;
        }
    });
    if (recursive) {
        _.forEach(comps, function (comp) { return restoreLayouts(comp.components, false); });
    }
}
function setComponentsOrder(parent, comps, settings) {
    var eventsY = createEventsQueue(comps, 'y');
    var eventsX = createEventsQueue(comps, 'x');
    var orderedComponents = getSortedComponents(parent, comps, { y: eventsY, x: eventsX }, settings);
    parent.conversionData.componentsOrder = _.map(orderedComponents, 'id');
}
exports.setComponentsOrder = setComponentsOrder;
var testAPI = {
    isEven: isEven,
    transformLayouts: transformLayouts,
    restoreLayouts: restoreLayouts,
    extractCompsFromEvents: extractCompsFromEvents,
    getPerpendicularAxis: getPerpendicularAxis,
    compareEvents: compareEvents,
    refragment: refragment,
    splitAxisEventsByFragments: splitAxisEventsByFragments,
    splitAllEventsByFragments: splitAllEventsByFragments,
    calculateMaxCompsDistance: calculateMaxCompsDistance,
    sortComponentsByNaturalOrder: analyzerUtils_1.sortComponentsByNaturalOrder
};
exports.testAPI = testAPI;



//////////////////
// WEBPACK FOOTER
// ./src/mobileCore/main/analyzer/sweepSortAnalyzer.ts
// module id = 6
// module chunks = 0 1